import type { RefundPlan } from '@/lib/services/refunds/refunds.types'
import { normalizeCurrencyValue as roundToCents, parseCurrency } from '@/lib/utils'

/** Where a refund's money goes: the part of a plan every refund quote carries. */
type RefundDestinations = Pick<
  RefundPlan,
  'destinations' | 'shortfall_total' | 'manual_total' | 'automatic_total'
>

export interface RefundDestinationLine {
  key: string
  label: string
  amount: number
  tone?: 'warning'
}

/** The plan's sums by tender, as the server splits them (money rule 4). */
export function getRefundDestinationTotals(refundPlan: RefundDestinations | null | undefined) {
  const cardDestinationTotal = parseCurrency(refundPlan?.automatic_total)
  const manualDestinationTotal = parseCurrency(refundPlan?.manual_total)
  const shortfallTotal = roundToCents(refundPlan?.shortfall_total ?? 0)

  return {
    cardDestinationTotal,
    manualDestinationTotal,
    shortfallTotal,
    hasCardDestination: cardDestinationTotal > 0,
    hasManualDestination: manualDestinationTotal > 0,
    hasShortfall: shortfallTotal > 0,
  }
}

/** Cards by name, cash and checks as one amount to hand back, and anything payments can't cover. */
export function getRefundDestinationPreviewLines(
  refundPlan: RefundDestinations | null | undefined
): RefundDestinationLine[] {
  const totals = getRefundDestinationTotals(refundPlan)
  const lines: RefundDestinationLine[] = []

  if (totals.hasCardDestination) {
    for (const destination of (refundPlan?.destinations ?? []).filter(
      (destination) => destination.method === 'card'
    )) {
      lines.push({
        key: `payment-${destination.source_payment_id}`,
        label: `Refund to ${destination.destination_label || 'original payment method'}`,
        amount: roundToCents(destination.amount),
      })
    }
  }
  if (totals.hasManualDestination) {
    lines.push({
      key: 'manual-destination-total',
      label: 'Hand back to customer (cash/check)',
      amount: totals.manualDestinationTotal,
    })
  }
  if (totals.hasShortfall) {
    lines.push({
      key: 'shortfall-total',
      label: 'Not covered by collected payments',
      amount: totals.shortfallTotal,
      tone: 'warning',
    })
  }

  return lines
}
